/**
 * Malcriados · pagina de producto.
 *
 * 1. Pedido: numero de retratos, unidades (prendas, jockeys, collares) con sus opciones reales,
 *    mejoras, total, ahorro y compra en una sola llamada a /cart/add.js.
 * 2. Galeria, carruseles con puntos, reseña destacada, filtro de reseñas y videos.
 * 3. "Cambiar región" (usa window.MC de assets/mc-base.js).
 *
 * Contrato con el carrito: cada unidad es una linea con cantidad 1 y las propiedades
 * _diseno y _prenda; cada mejora lleva _diseno y _mejora_de (el _prenda de su unidad).
 */

/** @param {number} centavos */
function dinero(centavos) {
  if (window.MC?.dinero) return window.MC.dinero(centavos);
  return '$' + String(Math.round(centavos / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** @param {string} texto */
function primerEntero(texto) {
  const hallado = String(texto).match(/\d+/);
  return hallado ? Number(hallado[0]) : 0;
}

/** @param {string} nombre */
function esOpcionRetratos(nombre) {
  return /retrato|mascota/i.test(nombre);
}

/**
 * @param {Element | null | undefined} nodo
 * @param {string} texto
 */
function escribir(nodo, texto) {
  if (nodo && nodo.textContent !== texto) nodo.textContent = texto;
}

/**
 * @param {Element | null | undefined} nodo
 * @param {boolean} oculto
 */
function ocultar(nodo, oculto) {
  if (nodo instanceof HTMLElement) nodo.hidden = oculto;
}

/* ============================================================
 * PEDIDO
 * ============================================================ */

class Pedido {
  /** @param {HTMLElement} raiz */
  constructor(raiz) {
    this.raiz = raiz;
    this.datos = JSON.parse(raiz.querySelector('[data-mc-pdp-datos]')?.textContent || '{}');
    this.form = /** @type {HTMLFormElement | null} */ (raiz.querySelector('.mc-pdp-form'));
    this.caja = /** @type {HTMLElement | null} */ (raiz.querySelector('[data-unidades]'));
    this.molde = /** @type {HTMLTemplateElement | null} */ (raiz.querySelector('[data-unidad-molde]'));
    this.alerta = raiz.querySelector('[data-alerta]');
    this.boton = /** @type {HTMLButtonElement | null} */ (raiz.querySelector('[data-comprar]'));

    const elegido = raiz.querySelector('[data-retrato][aria-pressed="true"]');
    /** @type {string | null} */
    this.retrato = elegido?.getAttribute('data-retrato') ?? this.#retratoUnico();
    this.nivel = 1;
    this.recordadas = 2;
    /** @type {Set<number>} */
    this.mejoras = new Set();
    /** @type {string[]} colores (o '' si el jockey no tiene color) de cada jockey agregado */
    this.jockeys = [];
    this.eligiendoJockey = false;
    this.enviando = false;
    this.mediaVista = 0;

    if (!this.form || !this.caja) return;

    raiz.addEventListener('click', (evento) => this.#alTocar(evento));
    raiz.addEventListener('change', (evento) => this.#alCambiar(evento));
    this.form.addEventListener('submit', (evento) => {
      evento.preventDefault();
      this.comprar();
    });

    this.pintar();
  }

  /* ---------- Datos ---------- */

  #retratoUnico() {
    const pos = this.datos.posRetratos;
    if (pos < 0) return null;
    return this.datos.variantes?.[0]?.o?.[pos] ?? null;
  }

  get unidades() {
    return /** @type {HTMLElement[]} */ (Array.from(this.caja?.querySelectorAll('[data-unidad]') ?? []));
  }

  get minimo() {
    return this.datos.niveles && this.nivel === 2 ? 2 : 1;
  }

  /**
   * Valores de todas las opciones para una unidad ('' si falta elegir).
   * @param {HTMLElement} unidad
   * @returns {string[]}
   */
  valores(unidad) {
    const valores = /** @type {string[]} */ ((this.datos.opciones || []).map(() => ''));
    if (this.datos.posRetratos >= 0) valores[this.datos.posRetratos] = this.retrato ?? '';
    for (const control of unidad.querySelectorAll('[data-opcion]')) {
      const indice = Number(control.getAttribute('data-opcion'));
      if (control instanceof HTMLSelectElement) valores[indice] = control.value;
      else valores[indice] = control.querySelector('[aria-pressed="true"]')?.getAttribute('data-valor') ?? '';
    }
    return valores;
  }

  /**
   * Variantes que calzan con los valores ya elegidos (los vacios no filtran).
   * @param {string[]} valores
   * @param {number} [ignorar] opcion que no se toma en cuenta
   */
  calzan(valores, ignorar = -1) {
    return /** @type {any[]} */ (this.datos.variantes || []).filter((v) =>
      valores.every((valor, i) => i === ignorar || valor === '' || v.o[i] === valor)
    );
  }

  /** @param {HTMLElement} unidad */
  variante(unidad) {
    const valores = this.valores(unidad);
    if (valores.some((valor) => valor === '')) return null;
    return this.calzan(valores)[0] ?? null;
  }

  /**
   * Variante cuya foto se muestra: la exacta si ya esta todo elegido; si falta algo (por ejemplo la talla),
   * la primera que coincida con lo elegido hasta ahora y tenga foto.
   * @param {HTMLElement} unidad
   * @param {'f' | 'm'} [campo] foto ('f') o imagen de la galeria ('m')
   */
  varianteParaFoto(unidad, campo = 'f') {
    const exacta = this.variante(unidad);
    if (exacta?.[campo]) return exacta;
    return this.calzan(this.valores(unidad)).find((v) => v[campo]) ?? null;
  }

  /** Variante de referencia para mostrar precios cuando falta elegir algo. */
  referencia() {
    const valores = /** @type {string[]} */ ((this.datos.opciones || []).map(() => ''));
    if (this.datos.posRetratos >= 0) valores[this.datos.posRetratos] = this.retrato ?? '';
    const candidatas = this.calzan(valores);
    return candidatas.find((v) => v.d) ?? candidatas[0] ?? this.datos.variantes?.[0] ?? { p: 0, c: 0 };
  }

  /** @param {HTMLElement} unidad */
  precio(unidad) {
    const exacta = this.variante(unidad);
    if (exacta) return exacta.p;
    const candidatas = this.calzan(this.valores(unidad));
    return (candidatas.find((v) => v.d) ?? candidatas[0] ?? this.referencia()).p;
  }

  /* ---------- Jockey con el mismo retrato ---------- */

  /** Variantes del jockey que corresponden al numero de retratos elegido. */
  variantesJockey() {
    const jockey = this.jockeyActual();
    if (!jockey) return [];
    const pos = /** @type {string[]} */ (jockey.opciones).findIndex(esOpcionRetratos);
    const numero = primerEntero(this.retrato ?? '') || 1;
    return /** @type {any[]} */ (jockey.variantes).filter((v) => v.d && (pos < 0 || primerEntero(v.o[pos]) === numero));
  }

  /**
   * Jockey que corresponde a los retratos elegidos. Si las mejoras del producto traen un jockey por cantidad
   * de retratos, se usa ese (con su propio precio) y, fuera de su rango, no se ofrece ninguno.
   * Si no hay, se usa el jockey fijo del bloque.
   */
  jockeyActual() {
    const lista = /** @type {any[]} */ (this.datos.jockeysPorRetrato || []);
    if (lista.length === 0) return this.datos.jockey;
    const n = primerEntero(this.retrato ?? '') || 1;
    return lista.find((j) => (!j.min || n >= j.min) && (!j.max || n <= j.max)) ?? null;
  }

  /**
   * Una mejora (como el nombre en la manga) vale solo para los retratos que indica su registro en las
   * mejoras del producto (minimo y maximo). Sin registro, o sin limites, vale para todos.
   * @param {number} pid producto de la mejora
   */
  mejoraPermitida(pid) {
    const lista = /** @type {any[]} */ (this.datos.extras || []).filter((e) => e.pid === pid);
    if (lista.length === 0) return true;
    const n = primerEntero(this.retrato ?? '') || 1;
    return lista.some((e) => (!e.min || n >= e.min) && (!e.max || n <= e.max));
  }

  posColorJockey() {
    const opciones = /** @type {string[]} */ (this.jockeyActual()?.opciones ?? []);
    return opciones.findIndex((nombre) => !esOpcionRetratos(nombre));
  }

  /** Porcentaje por llevar `cantidad` prendas (2 y 3 segun la tienda; sin tiers, el fijo del bloque). @param {number} cantidad */
  pctDe(cantidad) {
    const d = this.datos;
    if (cantidad < 2) return 0;
    if (d.tiers) return cantidad === 2 ? Number(d.tiers['2']) || 0 : Number(d.tiers['3']) || 0; /* de 3 a 6 rige el tier3 */
    return d.descuento > 0 ? d.descuento : 0;
  }

  /** Extra de custom.mc_extras para un producto, si lo hay. @param {number} pid */
  extraDe(pid) {
    const lista = /** @type {any[]} */ (this.datos.extras || []).filter((e) => e.pid === pid);
    const n = primerEntero(this.retrato ?? '') || 1;
    return lista.find((e) => (!e.min || n >= e.min) && (!e.max || n <= e.max)) ?? lista[0] ?? null;
  }

  /** Descuento (centavos) del upsell de un producto. @param {number} pid */
  descuentoExtra(pid) {
    return this.extraDe(pid)?.d ?? 0;
  }

  /** Extra "jockey" que corresponde al numero de retratos elegido. */
  extraJockey() {
    const n = primerEntero(this.retrato ?? '') || 1;
    return (
      /** @type {any[]} */ (this.datos.extras || []).find(
        (e) => /jockey/i.test(e.t) && (!e.min || n >= e.min) && (!e.max || n <= e.max)
      ) ?? null
    );
  }

  /** @param {string} color */
  varianteJockey(color) {
    const pos = this.posColorJockey();
    return this.variantesJockey().find((v) => pos < 0 || v.o[pos] === color) ?? null;
  }

  /* ---------- Eventos ---------- */

  /** @param {Event} evento */
  #alTocar(evento) {
    const origen = evento.target instanceof Element ? evento.target : null;
    if (!origen) return;

    const retrato = origen.closest('[data-retrato]');
    if (retrato instanceof HTMLButtonElement && !retrato.disabled) {
      this.retrato = retrato.getAttribute('data-retrato');
      return this.cambio();
    }

    const nivel = origen.closest('[data-nivel]');
    if (nivel) {
      const nuevo = Number(nivel.getAttribute('data-nivel'));
      if (this.nivel === 2 && nuevo === 1) this.recordadas = this.unidades.length;
      this.nivel = nuevo;
      this.ajustarCantidad(nuevo === 1 ? 1 : Math.max(2, this.recordadas));
      return this.cambio();
    }

    if (origen.closest('[data-otra-unidad]')) {
      this.ajustarCantidad(this.unidades.length + 1);
      return this.cambio();
    }

    const quitar = origen.closest('[data-unidad-quitar]');
    if (quitar) {
      quitar.closest('[data-unidad]')?.remove();
      return this.cambio();
    }

    const chip = origen.closest('.mc-pdp-chip');
    if (chip instanceof HTMLButtonElement && !chip.disabled && chip.closest('[data-unidad]')) {
      for (const otro of chip.parentElement?.querySelectorAll('.mc-pdp-chip') ?? []) {
        otro.setAttribute('aria-pressed', String(otro === chip));
      }
      return this.cambio();
    }

    const mejora = origen.closest('[data-mejora-boton]');
    if (mejora) {
      const id = Number(mejora.closest('[data-mejora]')?.getAttribute('data-mejora'));
      if (this.mejoras.has(id)) this.mejoras.delete(id);
      else this.mejoras.add(id);
      return this.cambio();
    }

    if (origen.closest('[data-jockey-empezar]')) {
      if (this.posColorJockey() < 0) this.jockeys.push('');
      else this.eligiendoJockey = true;
      return this.cambio();
    }
    if (origen.closest('[data-jockey-cancelar]')) {
      this.eligiendoJockey = false;
      return this.cambio();
    }
    if (origen.closest('[data-jockey-quitar]')) {
      this.jockeys.pop();
      this.eligiendoJockey = false;
      return this.cambio();
    }
    const color = origen.closest('[data-jockey-color]');
    if (color) {
      this.jockeys.push(color.getAttribute('data-jockey-color') ?? '');
      this.eligiendoJockey = false;
      return this.cambio();
    }
  }

  /** @param {Event} evento */
  #alCambiar(evento) {
    if (evento.target instanceof HTMLSelectElement && evento.target.closest('[data-unidad]')) this.cambio();
  }

  /** Cualquier cambio del pedido limpia avisos y vuelve a pintar. */
  cambio() {
    this.avisar('');
    this.listo(false);
    this.pintar();
  }

  /** @param {number} cantidad */
  ajustarCantidad(cantidad) {
    if (!this.caja || !this.molde) return;
    const meta = Math.max(1, Math.min(cantidad, this.datos.maximo || 1));
    while (this.unidades.length > meta) this.unidades[this.unidades.length - 1]?.remove();
    while (this.unidades.length < meta) {
      const primera = this.unidades[0];
      const nueva = /** @type {HTMLElement | null} */ (
        /** @type {DocumentFragment} */ (this.molde.content.cloneNode(true)).querySelector('[data-unidad]')
      );
      if (!nueva) break;
      /* Igual que la maqueta: la unidad nueva parte con lo elegido en la primera. */
      if (primera) {
        const elegidos = this.valores(primera);
        for (const control of nueva.querySelectorAll('[data-opcion]')) {
          const valor = elegidos[Number(control.getAttribute('data-opcion'))];
          if (control instanceof HTMLSelectElement) control.value = valor;
          else {
            for (const chip of control.querySelectorAll('.mc-pdp-chip')) {
              chip.setAttribute('aria-pressed', String(chip.getAttribute('data-valor') === valor));
            }
          }
        }
      }
      nueva.classList.add('mc-pdp-entra');
      this.caja.appendChild(nueva);
    }
  }

  /* ---------- Pintado ---------- */

  pintar() {
    const d = this.datos;
    const raiz = this.raiz;

    /* Unidades: cantidad valida, etiquetas, disponibilidad y miniatura. */
    if (this.unidades.length < this.minimo) this.ajustarCantidad(this.minimo);
    const unidades = this.unidades;
    const cantidad = unidades.length;
    if (d.niveles && this.nivel === 2) this.recordadas = cantidad;

    unidades.forEach((unidad, i) => {
      const numero = i + 1;
      const nombreUnidad = String(d.unidad);
      escribir(unidad.querySelector('[data-unidad-nombre]'), `${nombreUnidad.charAt(0).toUpperCase()}${nombreUnidad.slice(1)} ${numero}`);
      ocultar(unidad.querySelector('[data-unidad-quitar]'), i < this.minimo);
      for (const control of unidad.querySelectorAll('[data-opcion]')) {
        const nombre = control.getAttribute('data-nombre') ?? '';
        control.setAttribute('aria-label', `${nombre} de ${d.unidad} ${numero}`);
      }
      this.#disponibilidad(unidad);
      for (const selector of unidad.querySelectorAll('[data-selector]')) sincronizarSelector(selector);
      /* La foto sigue a lo elegido hasta ahora (retratos, color...): no hace falta completar todo. */
      const variante = this.varianteParaFoto(unidad);
      const foto = unidad.querySelector('[data-unidad-foto] img');
      if (variante?.f && foto instanceof HTMLImageElement && foto.getAttribute('src') !== variante.f) {
        foto.removeAttribute('srcset');
        foto.src = variante.f;
      }
    });

    /* La galeria sigue a la variante de la primera unidad cuando tiene foto propia. */
    const primera = unidades[0] ? this.varianteParaFoto(unidades[0], 'm') : null;
    if (primera?.m && primera.m !== this.mediaVista) {
      if (this.mediaVista !== 0) raiz.dispatchEvent(new CustomEvent('mc-pdp:media', { detail: { id: primera.m } }));
      this.mediaVista = primera.m;
    }

    /* Retratos y niveles. */
    for (const boton of raiz.querySelectorAll('[data-retrato]')) {
      boton.setAttribute('aria-pressed', String(boton.getAttribute('data-retrato') === this.retrato));
    }
    for (const boton of raiz.querySelectorAll('[data-nivel]')) {
      boton.setAttribute('aria-pressed', String(Number(boton.getAttribute('data-nivel')) === this.nivel));
    }
    const base = this.referencia().p;
    const pct = this.pctDe(cantidad);
    const conDescuento = pct > 0;
    const rebajar = (/** @type {number} */ precio, /** @type {number} */ porcentaje = pct) =>
      Math.round((precio * (100 - porcentaje)) / 100);
    escribir(raiz.querySelector('[data-precio-base]'), dinero(base));
    escribir(raiz.querySelector('[data-cuota-base]'), dinero(base / d.cuotas));
    escribir(raiz.querySelector('[data-nivel-precio-1]'), dinero(base));
    const pct2 = this.pctDe(2);
    escribir(raiz.querySelector('[data-nivel-precio-2]'), dinero(rebajar(base, pct2)));
    escribir(raiz.querySelector('[data-nivel-antes]'), dinero(base));
    escribir(raiz.querySelector('[data-nivel-ahorro]'), dinero(base * 2 - rebajar(base, pct2) * 2));

    /* La condicion de la oferta solo se muestra con "2 o 3 prendas". */
    ocultar(raiz.querySelector('[data-oferta]'), this.nivel !== 2);
    const puedeSumar = cantidad < d.maximo && (!d.niveles || this.nivel === 2);
    ocultar(raiz.querySelector('[data-otra-unidad]'), !puedeSumar);
    /* El boton dice cuanto descuento da la siguiente: con tramos, el de llevar una mas. */
    const siguiente = this.pctDe(cantidad + 1);
    if (siguiente > 0) {
      escribir(raiz.querySelector('[data-otra-pct]'), `${siguiente}% de descuento`);
    }

    /* Totales. */
    const precios = unidades.map((unidad) => this.precio(unidad));
    const normal = precios.reduce((suma, precio) => suma + precio, 0);
    const totalUnidades = conDescuento ? precios.reduce((suma, precio) => suma + rebajar(precio), 0) : normal;
    let total = totalUnidades;
    let ahorro = normal - totalUnidades;

    /* Mejoras: una por unidad. */
    const enPlural = cantidad > 1 ? `en ${d.articulo} ${cantidad}` : '';
    for (const tarjeta of raiz.querySelectorAll('[data-mejora]')) {
      const id = Number(tarjeta.getAttribute('data-mejora'));
      const mejora = /** @type {any[]} */ (d.mejoras).find((m) => m.id === id);
      if (!mejora) continue;
      const permitida = this.mejoraPermitida(mejora.pid);
      ocultar(tarjeta, !permitida);
      if (!permitida) {
        this.mejoras.delete(id);
        continue;
      }
      const activa = this.mejoras.has(id);
      tarjeta.classList.toggle('mc-pdp-mejora--lista', activa);
      escribir(tarjeta.querySelector('[data-mejora-nombre]'), activa ? `${mejora.titulo} agregado` : mejora.titulo);
      const desc = this.descuentoExtra(mejora.pid);
      const unitario = mejora.p - desc;
      escribir(tarjeta.querySelector('[data-mejora-precio]'), '+' + dinero(activa ? unitario * cantidad : unitario));
      const antesMejora = tarjeta.querySelector('[data-mejora-antes]');
      escribir(antesMejora, desc > 0 ? '+' + dinero(activa ? mejora.p * cantidad : mejora.p) : '');
      ocultar(antesMejora, desc <= 0);
      escribir(tarjeta.querySelector('[data-mejora-nota]'), activa ? enPlural : '');
      escribir(tarjeta.querySelector('[data-mejora-boton-texto]'), activa ? 'Quitar' : 'Agregar');
      tarjeta.querySelector('[data-mejora-boton]')?.setAttribute('aria-pressed', String(activa));
      if (activa) {
        total += unitario * cantidad;
        ahorro += desc * cantidad;
      }
    }

    /* Jockey. */
    const extra = this.#pintarJockey(cantidad);
    total += extra.total;
    ahorro += extra.ahorro;

    escribir(raiz.querySelector('[data-total]'), dinero(total));
    escribir(raiz.querySelector('[data-total-cuota]'), dinero(total / d.cuotas));
    escribir(raiz.querySelector('[data-barra-total]'), dinero(total));
    const antes = raiz.querySelector('[data-total-antes]');
    escribir(antes, dinero(total + ahorro));
    ocultar(antes, ahorro <= 0);
    escribir(raiz.querySelector('[data-ahorro-monto]'), dinero(ahorro));
    ocultar(raiz.querySelector('[data-ahorro]'), ahorro <= 0);

    /* Envio gratis. */
    const envio = raiz.querySelector('[data-envio]');
    if (envio) {
      const gratis = total >= d.gratisDesde;
      envio.classList.toggle('mc-pdp-incluye__item--falta', !gratis);
      ocultar(envio.querySelector('[data-envio-gratis]'), !gratis);
      ocultar(envio.querySelector('[data-envio-falta]'), gratis);
      escribir(envio.querySelector('[data-envio-monto]'), dinero(Math.max(d.gratisDesde - total, 0)));
    }
  }

  /**
   * Deshabilita los valores sin variante disponible para lo ya elegido en la unidad.
   * @param {HTMLElement} unidad
   */
  #disponibilidad(unidad) {
    for (const control of unidad.querySelectorAll('[data-opcion]')) {
      const indice = Number(control.getAttribute('data-opcion'));
      const valores = this.valores(unidad);
      const hay = (/** @type {string} */ valor) =>
        this.calzan(valores, indice).some((v) => v.d && v.o[indice] === valor);

      if (control instanceof HTMLSelectElement) {
        for (const opcion of control.options) {
          if (opcion.value === '') continue;
          const disponible = hay(opcion.value);
          opcion.disabled = !disponible;
          escribir(opcion, disponible ? opcion.value : `${opcion.value} (agotado)`);
        }
        if (control.selectedOptions[0]?.disabled) {
          const libre = control.hasAttribute('data-talla')
            ? null
            : Array.from(control.options).find((opcion) => !opcion.disabled && opcion.value !== '');
          control.value = libre ? libre.value : control.hasAttribute('data-talla') ? '' : control.value;
        }
      } else {
        const chips = /** @type {HTMLButtonElement[]} */ (Array.from(control.querySelectorAll('.mc-pdp-chip')));
        for (const chip of chips) chip.disabled = !hay(chip.getAttribute('data-valor') ?? '');
        const elegido = chips.find((chip) => chip.getAttribute('aria-pressed') === 'true');
        if (!elegido || elegido.disabled) {
          const libre = chips.find((chip) => !chip.disabled);
          for (const chip of chips) chip.setAttribute('aria-pressed', String(chip === libre));
        }
      }
    }
  }

  /**
   * @param {number} cantidad unidades del pedido (tope de jockeys)
   * @returns {{ total: number, ahorro: number }}
   */
  #pintarJockey(cantidad) {
    const tarjeta = this.raiz.querySelector('[data-jockey]');
    if (!tarjeta) return { total: 0, ahorro: 0 };

    const ofrecidas = this.variantesJockey();
    ocultar(tarjeta, ofrecidas.length === 0);
    if (ofrecidas.length > 0) {
      /* La foto y el nombre de la tarjeta son los del jockey que corresponde a estos retratos. */
      const foto = ofrecidas.find((v) => v.f)?.f;
      const mini = tarjeta.querySelector('[data-jockey-estado="inicio"] .mc-pdp-mini img');
      if (foto && mini instanceof HTMLImageElement && mini.getAttribute('src') !== foto) {
        mini.removeAttribute('srcset');
        mini.src = foto;
      }
      const nombre = tarjeta.querySelector('[data-jockey-estado="inicio"] .mc-pdp-mejora__nombre');
      if (nombre instanceof HTMLElement) {
        nombre.dataset.base ??= nombre.textContent ?? '';
        const varios = (primerEntero(this.retrato ?? '') || 1) > 1;
        escribir(nombre, varios ? nombre.dataset.base.replace(/el mismo retrato/i, 'los mismos retratos') : nombre.dataset.base);
      }
    }
    if (ofrecidas.length === 0) {
      this.jockeys = [];
      this.eligiendoJockey = false;
      return { total: 0, ahorro: 0 };
    }

    /* Hasta dos jockeys, o uno por prenda si son mas prendas, siempre con el mismo diseño. */
    const tope = Math.max(cantidad, 2);
    this.jockeys = this.jockeys.filter((color) => this.varianteJockey(color)).slice(0, tope);
    const elegidas = this.jockeys.map((color) => this.varianteJockey(color));
    const cuenta = elegidas.length;
    const eligiendo = this.eligiendoJockey && cuenta < tope;
    this.eligiendoJockey = eligiendo;

    const estado = eligiendo ? 'eligiendo' : cuenta > 0 ? 'agregado' : 'inicio';
    for (const parte of tarjeta.querySelectorAll('[data-jockey-estado]')) {
      ocultar(parte, parte.getAttribute('data-jockey-estado') !== estado);
    }
    tarjeta.classList.toggle('mc-pdp-mejora--lista', cuenta > 0);

    const descJockey = this.extraJockey()?.d ?? 0;
    const barata = ofrecidas.reduce((a, b) => (b.p < a.p ? b : a));
    escribir(tarjeta.querySelector('[data-jockey-precio]'), '+' + dinero(Math.max(barata.p - descJockey, 0)));
    const antes = tarjeta.querySelector('[data-jockey-antes]');
    const antesPrecio = descJockey > 0 ? barata.p : barata.c > barata.p ? barata.c : 0;
    escribir(antes, antesPrecio ? dinero(antesPrecio) : '');
    ocultar(antes, !antesPrecio);

    if (eligiendo) {
      escribir(
        tarjeta.querySelector('[data-jockey-pregunta]'),
        cuenta === 0 ? 'Elige el color del jockey' : 'Elige el color del otro jockey'
      );
      const caja = tarjeta.querySelector('[data-jockey-colores]');
      const pos = this.posColorJockey();
      const colores = [...new Set(ofrecidas.map((v) => v.o[pos]))];
      if (caja && caja.getAttribute('data-colores') !== colores.join('|')) {
        caja.setAttribute('data-colores', colores.join('|'));
        const conFotos = colores.some((color) => ofrecidas.find((v) => v.o[pos] === color && v.f));
        caja.classList.toggle('mc-pdp-chips', conFotos);
        caja.classList.toggle('mc-pdp-chips--cartas', conFotos);
        caja.replaceChildren(
          ...colores.map((color) => {
            const boton = document.createElement('button');
            boton.type = 'button';
            boton.className = 'mc-pdp-chip';
            boton.setAttribute('data-jockey-color', color);
            const foto = ofrecidas.find((v) => v.o[pos] === color && v.f)?.f;
            if (foto) {
              /* Con foto en la variante se elige con la imagen de cada color, no solo con su nombre. */
              boton.className = 'mc-pdp-chip mc-pdp-chip--carta';
              const marco = document.createElement('span');
              marco.className = 'mc-pdp-chip__foto';
              const imagen = document.createElement('img');
              imagen.src = foto;
              imagen.alt = '';
              imagen.width = 120;
              imagen.height = 120;
              marco.append(imagen);
              const nombreColor = document.createElement('span');
              nombreColor.className = 'mc-pdp-chip__nombre';
              nombreColor.textContent = color;
              boton.append(marco, nombreColor);
              return boton;
            }
            const tono = this.jockeyActual()?.colores?.[color];
            if (tono) {
              const punto = document.createElement('span');
              punto.className = 'mc-pdp-chip__tono';
              punto.style.backgroundColor = tono;
              boton.append(punto);
            }
            boton.append(color);
            return boton;
          })
        );
      }
    }

    const total = elegidas.reduce((suma, v) => suma + Math.max(v.p - descJockey, 0), 0);
    const ahorro = elegidas.reduce((suma, v) => suma + (descJockey > 0 ? descJockey : v.c > v.p ? v.c - v.p : 0), 0);
    if (cuenta > 0) {
      const lista = this.jockeys.filter(Boolean).join(', ').toLowerCase();
      const titulo =
        cuenta > 1 ? `${cuenta} jockeys agregados` : `Jockey ${this.jockeys[0].toLowerCase()} agregado`.replace('  ', ' ');
      escribir(tarjeta.querySelector('[data-jockey-titulo]'), titulo);
      /* La miniatura de "agregado" es la del color elegido, no la del producto. */
      const fotoElegida = elegidas[0]?.f;
      const miniatura = tarjeta.querySelector('[data-jockey-estado="agregado"] .mc-pdp-mini img');
      if (fotoElegida && miniatura instanceof HTMLImageElement && miniatura.getAttribute('src') !== fotoElegida) {
        miniatura.removeAttribute('srcset');
        miniatura.src = fotoElegida;
      }
      const detalle = tarjeta.querySelector('[data-jockey-detalle]');
      if (detalle) {
        /* Mismo estilo que antes de agregar: precio en negrita y, si hay descuento, el precio normal tachado. */
        const nodos = [];
        if (cuenta > 1 && lista) {
          nodos.push(document.createTextNode(lista.charAt(0).toUpperCase() + lista.slice(1) + ' · '));
        }
        const fuerte = document.createElement('strong');
        fuerte.textContent = '+' + dinero(total);
        nodos.push(fuerte);
        if (ahorro > 0) {
          nodos.push(document.createTextNode(' '));
          const antesNodo = document.createElement('s');
          antesNodo.className = 'mc-pdp-suave';
          antesNodo.textContent = dinero(total + ahorro);
          nodos.push(antesNodo);
        }
        detalle.replaceChildren(...nodos);
      }
    }
    ocultar(tarjeta.querySelector('[data-jockey-mas]'), !(cuenta > 0 && cuenta < tope));

    return { total, ahorro };
  }

  /** @param {string} mensaje */
  avisar(mensaje) {
    escribir(this.alerta, mensaje);
    ocultar(this.alerta, mensaje === '');
  }

  /** @param {boolean} hecho */
  listo(hecho) {
    ocultar(this.raiz.querySelector('[data-comprar-normal]'), hecho);
    ocultar(this.raiz.querySelector('[data-comprar-listo]'), !hecho);
  }

  /* ---------- Compra ---------- */

  /**
   * Propiedades de un extra (mejora o jockey) ligado a una prenda.
   * @param {string} diseno
   * @param {string} prenda
   * @param {number} posicion
   * @param {any} extra definicion en custom.mc_extras, si la hay
   * @returns {Record<string, string>}
   */
  propiedadesExtra(diseno, prenda, posicion, extra) {
    /** @type {Record<string, string>} */
    const props = {
      _diseno: diseno,
      _mejora_de: prenda,
      __mc_pedido_id: diseno,
      __mc_pack_id: diseno,
      __mc_extra_for_pos: String(posicion),
      __mc_tipo: 'extra',
      'Para prenda': String(posicion),
    };
    if (extra && extra.i) props.__mc_extra_id = String(extra.i);
    if (extra && extra.d > 0) props._mc_extra_discount_clp = String(Math.round(extra.d / 100));
    return props;
  }

  /** Lineas del carrito segun el contrato, o null si falta elegir algo. */
  lineas() {
    const diseno = 'd' + Date.now().toString(36);
    /** @type {Array<{ id: number, quantity: number, properties: Record<string, string> }>} */
    const lineas = [];
    /** @type {typeof lineas} */
    const mejoras = [];
    const unidades = this.unidades;

    for (const [i, unidad] of unidades.entries()) {
      const valores = this.valores(unidad);
      const falta = valores.findIndex((valor) => valor === '');
      if (falta >= 0) {
        const control = /** @type {HTMLElement | null} */ (unidad.querySelector(`[data-opcion="${falta}"]`));
        const nombre = (control?.getAttribute('data-nombre') ?? 'una opción').toLowerCase();
        this.avisar(nombre === 'talla' ? 'Elige la talla para continuar' : `Elige ${nombre} para continuar`);
        control?.focus();
        return null;
      }
      const variante = this.calzan(valores)[0];
      if (!variante || !variante.d) {
        this.avisar('Esa combinación no está disponible. Elige otra para continuar.');
        return null;
      }
      const prenda = `${diseno}-${i + 1}`;
      /* Propiedades del sistema de packs (las lee la app de descuentos malcriados-discounts y n8n):
         __mc_pedido_id / __mc_pack_id agrupan el pedido, __mc_pack_pos es la posicion de la prenda y
         _mc_tier_group / _mc_tier_qty activan el descuento por volumen (2 o 3 prendas del mismo diseno). */
      /** @type {Record<string, string>} */
      const propiedades = {
        _diseno: diseno,
        _prenda: prenda,
        __mc_pedido_id: diseno,
        __mc_pack_id: diseno,
        __mc_pack_total: String(unidades.length),
        __mc_pack_pos: String(i + 1),
      };
      if (this.datos.tiers && unidades.length >= 2 && unidades.length <= 6) {
        propiedades._mc_tier_group = diseno;
        propiedades._mc_tier_qty = String(unidades.length);
      }
      lineas.push({ id: variante.id, quantity: 1, properties: propiedades });
      for (const id of this.mejoras) {
        const mejora = /** @type {any} */ ((this.datos.mejoras || []).find((/** @type {any} */ m) => m.id === id));
        const extra = mejora ? this.extraDe(mejora.pid) : null;
        mejoras.push({ id, quantity: 1, properties: this.propiedadesExtra(diseno, prenda, i + 1, extra) });
      }
    }

    this.jockeys.forEach((color, i) => {
      const variante = this.varianteJockey(color);
      if (!variante) return;
      /* Con mas jockeys que prendas, el segundo va ligado a la misma prenda (mismo diseño). */
      const pos = (i % Math.max(unidades.length, 1)) + 1;
      mejoras.push({
        id: variante.id,
        quantity: 1,
        /* Un identificador propio evita que dos jockeys del mismo color se junten en una sola linea (cantidad 2),
           porque el descuento de regalo solo se aplica a extras de cantidad 1. */
        properties: { ...this.propiedadesExtra(diseno, `${diseno}-${pos}`, pos, this.extraJockey()), _extra_uid: `${i + 1}` },
      });
    });

    return [...lineas, ...mejoras];
  }

  async comprar() {
    if (this.enviando || !this.datos.disponible) return;
    const lineas = this.lineas();
    if (!lineas) return;

    this.enviando = true;
    this.avisar('');
    this.boton?.setAttribute('aria-busy', 'true');

    const rutas = window.Theme?.routes ?? {};
    const urlAgregar = rutas.cart_add_url || '/cart/add.js';
    const urlCarrito = rutas.cart_url || '/cart';

    /* Mismos eventos que el formulario de producto de Horizon: actualizan contador y panel. */
    /** @type {any} */
    let eventos = null;
    try {
      eventos = await import('@shopify/events');
    } catch {
      eventos = null;
    }
    const diferido = eventos?.CartLinesUpdateEvent?.createPromise?.() ?? null;
    if (diferido && eventos) {
      this.form?.dispatchEvent(
        new eventos.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lineas.map((linea) => ({ merchandiseId: String(linea.id), quantity: linea.quantity })),
          promise: diferido.promise,
        })
      );
    }

    const secciones = [];
    for (const componente of document.querySelectorAll('cart-items-component')) {
      if (componente instanceof HTMLElement && componente.dataset.sectionId) secciones.push(componente.dataset.sectionId);
    }

    const leerCarrito = () =>
      fetch(`${urlCarrito}.js`, { headers: { Accept: 'application/json' } }).then((respuesta) => respuesta.json());
    const detalle = { source: 'mc-pdp', sourceId: this.raiz.dataset.sectionId ?? '', itemCount: lineas.length, productId: String(this.datos.producto) };

    try {
      const respuesta = await fetch(urlAgregar, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lineas, sections: secciones.join(','), sections_url: window.location.pathname }),
      });
      const cuerpo = await respuesta.json();

      if (!respuesta.ok || cuerpo.status) {
        const mensaje =
          (typeof cuerpo.description === 'string' && cuerpo.description) ||
          (typeof cuerpo.message === 'string' && cuerpo.message) ||
          'No pudimos agregar tu pedido. Inténtalo de nuevo.';
        this.avisar(mensaje);
        if (eventos?.CartErrorEvent) {
          this.form?.dispatchEvent(
            new eventos.CartErrorEvent({ error: mensaje, code: 'INVALID', detail: { description: cuerpo.description, errors: cuerpo.errors } })
          );
        }
        if (diferido) {
          leerCarrito()
            .then((carrito) =>
              diferido.resolve({
                cart: eventos.CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
                detail: { ...detalle, items: carrito.items, didError: true },
              })
            )
            .catch(diferido.reject);
        }
        return;
      }

      if (!diferido) {
        /* Sin los eventos de Horizon no hay como refrescar el panel: se va al carrito. */
        window.location.href = urlCarrito;
        return;
      }

      const carrito = await leerCarrito();
      diferido.resolve({
        cart: eventos.CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: { ...detalle, items: carrito.items, sections: cuerpo.sections, didError: false },
      });
      this.listo(true);

      /* Abre el panel aunque la apertura automatica este apagada en el tema. */
      const panel = /** @type {any} */ (document.getElementById('cart-drawer'));
      requestAnimationFrame(() => {
        if (panel && typeof panel.open === 'function' && !panel.isOpen) panel.open();
      });
    } catch (error) {
      console.error(error);
      diferido?.reject(error);
      this.avisar('No pudimos agregar tu pedido. Revisa tu conexión e inténtalo de nuevo.');
      if (eventos?.CartErrorEvent) {
        this.form?.dispatchEvent(new eventos.CartErrorEvent({ error: String(error), code: 'SERVICE_UNAVAILABLE' }));
      }
    } finally {
      this.enviando = false;
      this.boton?.removeAttribute('aria-busy');
    }
  }
}

/* ============================================================
 * SELECTOR PROPIO (talla, color y demas listas)
 * ============================================================
 * La lista nativa de un <select> no se puede estilizar ni mostrar colores. El <select> real se
 * conserva (es el que lee el resto del codigo) y este selector lo refleja:
 * al elegir una opcion se cambia el <select> y se avisa con "change".
 */

/** @param {Element} selector */
function sincronizarSelector(selector) {
  const select = selector.querySelector('select');
  const boton = selector.querySelector('.mc-pdp-selector__boton');
  if (!(select instanceof HTMLSelectElement) || !boton) return;
  const opcion = select.selectedOptions[0];
  const muestra = boton.querySelector('.mc-pdp-selector__muestra');
  const hex = opcion?.dataset.hex ?? '';
  escribir(boton.querySelector('.mc-pdp-selector__texto'), opcion?.textContent?.trim() ?? '');
  if (muestra instanceof HTMLElement) {
    muestra.style.backgroundColor = hex;
    muestra.hidden = !(select.value && hex);
  }
}

/** @param {Element | null} [salvo] */
function cerrarSelectores(salvo = null) {
  for (const lista of document.querySelectorAll('.mc-pdp-selector__lista:not([hidden])')) {
    if (lista === salvo) continue;
    /** @type {HTMLElement} */ (lista).hidden = true;
    lista.closest('[data-selector]')?.querySelector('.mc-pdp-selector__boton')?.setAttribute('aria-expanded', 'false');
  }
}

/** @param {Element} selector */
function abrirSelector(selector) {
  const select = selector.querySelector('select');
  const boton = selector.querySelector('.mc-pdp-selector__boton');
  const lista = /** @type {HTMLElement | null} */ (selector.querySelector('.mc-pdp-selector__lista'));
  if (!(select instanceof HTMLSelectElement) || !boton || !lista) return;
  const items = Array.from(select.options)
    .filter((opcion) => opcion.value !== '')
    .map((opcion) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.valor = opcion.value;
      item.setAttribute('aria-selected', String(opcion.selected));
      if (opcion.disabled) item.setAttribute('aria-disabled', 'true');
      const muestra = document.createElement('span');
      muestra.className = 'mc-pdp-selector__muestra';
      if (opcion.dataset.hex) muestra.style.backgroundColor = opcion.dataset.hex;
      else muestra.hidden = true;
      const texto = document.createElement('span');
      texto.textContent = opcion.textContent?.trim() ?? '';
      item.append(muestra, texto);
      return item;
    });
  lista.replaceChildren(...items);
  lista.hidden = false;
  boton.setAttribute('aria-expanded', 'true');
  const actual = items.find((item) => item.getAttribute('aria-selected') === 'true') ?? items.find((item) => item.getAttribute('aria-disabled') !== 'true');
  actual?.focus({ preventScroll: true });
}

/** @param {HTMLElement} item */
function elegirEnSelector(item) {
  if (item.getAttribute('aria-disabled') === 'true') return;
  const selector = item.closest('[data-selector]');
  const select = selector?.querySelector('select');
  if (!selector || !(select instanceof HTMLSelectElement)) return;
  select.value = item.dataset.valor ?? '';
  select.dispatchEvent(new Event('change', { bubbles: true }));
  sincronizarSelector(selector);
  cerrarSelectores();
  /** @type {HTMLElement | null} */ (selector.querySelector('.mc-pdp-selector__boton'))?.focus();
}

document.addEventListener('click', (evento) => {
  const origen = evento.target instanceof Element ? evento.target : null;
  if (!origen) return;
  const boton = origen.closest('.mc-pdp-selector__boton');
  if (boton) {
    const selector = boton.closest('[data-selector]');
    const lista = selector?.querySelector('.mc-pdp-selector__lista');
    const estabaAbierta = lista instanceof HTMLElement && !lista.hidden;
    cerrarSelectores();
    if (selector && !estabaAbierta) abrirSelector(selector);
    return;
  }
  const item = origen.closest('.mc-pdp-selector__lista li');
  if (item instanceof HTMLElement) return elegirEnSelector(item);
  cerrarSelectores();
});

document.addEventListener('keydown', (evento) => {
  const origen = evento.target instanceof Element ? evento.target : null;
  if (evento.key === 'Escape') {
    const abierta = document.querySelector('.mc-pdp-selector__lista:not([hidden])');
    if (abierta) {
      cerrarSelectores();
      /** @type {HTMLElement | null} */ (abierta.closest('[data-selector]')?.querySelector('.mc-pdp-selector__boton'))?.focus();
    }
    return;
  }
  const item = origen?.closest('.mc-pdp-selector__lista li');
  if (!(item instanceof HTMLElement)) return;
  if (evento.key === 'Enter' || evento.key === ' ') {
    evento.preventDefault();
    elegirEnSelector(item);
  } else if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
    evento.preventDefault();
    const hermano = evento.key === 'ArrowDown' ? item.nextElementSibling : item.previousElementSibling;
    if (hermano instanceof HTMLElement) hermano.focus({ preventScroll: false });
  }
});

/* ============================================================
 * GALERIA
 * ============================================================ */

/** @param {HTMLElement} raiz */
function iniciarGaleria(raiz) {
  const galeria = raiz.querySelector('[data-galeria]');
  const pista = /** @type {HTMLElement | null} */ (galeria?.querySelector('[data-galeria-pista]') ?? null);
  if (!galeria || !pista) return;

  const fotos = /** @type {HTMLElement[]} */ (Array.from(pista.children));
  const minis = Array.from(galeria.querySelectorAll('[data-galeria-mini]'));
  const cuenta = galeria.querySelector('[data-galeria-cuenta]');
  const actual = () => Math.round(pista.scrollLeft / Math.max(pista.clientWidth, 1));
  const suave = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  /** @param {number} indice */
  const ir = (indice) => {
    const destino = (indice + fotos.length) % fotos.length;
    pista.scrollTo({ left: destino * pista.clientWidth, behavior: suave() });
  };

  const marcar = () => {
    const indice = actual();
    escribir(cuenta, `${indice + 1} / ${fotos.length}`);
    minis.forEach((mini, i) => mini.setAttribute('aria-pressed', String(i === indice)));
    /* La fila de miniaturas acompaña a la foto: deja a la activa a la vista. */
    const activa = /** @type {HTMLElement | undefined} */ (minis[indice]);
    const fila = activa?.parentElement;
    if (activa && fila && fila.scrollWidth > fila.clientWidth) {
      fila.scrollTo({ left: activa.offsetLeft - (fila.clientWidth - activa.offsetWidth) / 2, behavior: suave() });
    }
  };

  /* Flechas de la fila de miniaturas: solo se ven si hay mas a ese lado. */
  const filaMinis = /** @type {HTMLElement | null} */ (galeria.querySelector('[data-galeria-minis]'));
  const flechaPrev = /** @type {HTMLElement | null} */ (galeria.querySelector('[data-minis-prev]'));
  const flechaNext = /** @type {HTMLElement | null} */ (galeria.querySelector('[data-minis-next]'));
  const flechasMinis = () => {
    if (!filaMinis) return;
    const maximo = filaMinis.scrollWidth - filaMinis.clientWidth;
    if (flechaPrev) flechaPrev.hidden = filaMinis.scrollLeft <= 2;
    if (flechaNext) flechaNext.hidden = filaMinis.scrollLeft >= maximo - 2;
  };
  if (filaMinis) {
    const mover = (/** @type {number} */ sentido) =>
      filaMinis.scrollBy({ left: sentido * filaMinis.clientWidth * 0.8, behavior: suave() });
    flechaPrev?.addEventListener('click', () => mover(-1));
    flechaNext?.addEventListener('click', () => mover(1));
    filaMinis.addEventListener('scroll', flechasMinis, { passive: true });
    window.addEventListener('resize', flechasMinis);
    flechasMinis();
  }

  let pendiente = 0;
  pista.addEventListener('scroll', () => {
    cancelAnimationFrame(pendiente);
    pendiente = requestAnimationFrame(marcar);
  });
  galeria.querySelector('[data-galeria-prev]')?.addEventListener('click', () => ir(actual() - 1));
  galeria.querySelector('[data-galeria-next]')?.addEventListener('click', () => ir(actual() + 1));
  minis.forEach((mini, i) => mini.addEventListener('click', () => ir(i)));

  raiz.addEventListener('mc-pdp:media', (evento) => {
    const id = String(/** @type {CustomEvent} */ (evento).detail?.id ?? '');
    const indice = fotos.findIndex((foto) => foto.getAttribute('data-media') === id);
    if (indice >= 0) ir(indice);
  });
}

/* ============================================================
 * BARRA FIJA DE COMPRA (celular)
 * ============================================================ */

/** @param {HTMLElement} raiz */
function iniciarBarra(raiz) {
  const barra = /** @type {HTMLElement | null} */ (raiz.querySelector('[data-barra]'));
  const boton = raiz.querySelector('[data-comprar]');
  if (!barra || !boton || !('IntersectionObserver' in window)) return;

  /* Solo se muestra cuando el boton de compra ya quedo arriba (se paso hacia
     abajo) y no se esta viendo el pie de pagina. Antes de llegar al boton
     no aparece. */
  let botonPasado = false;
  let pieVisible = false;
  const pie = document.querySelector('.mc-pie');
  const observador = new IntersectionObserver((entradas) => {
    for (const entrada of entradas) {
      if (entrada.target === boton) {
        botonPasado = !entrada.isIntersecting && entrada.boundingClientRect.top < 0;
      } else {
        pieVisible = entrada.isIntersecting;
      }
    }
    barra.hidden = !botonPasado || pieVisible;
  });
  observador.observe(boton);
  if (pie) observador.observe(pie);
}

/* ============================================================
 * PIEZAS DE LAS SECCIONES DE CONTENIDO
 * ============================================================ */

/** Carruseles: puntos, flechas y pestañas que llevan a un elemento. */
function iniciarCarruseles() {
  for (const carrusel of document.querySelectorAll('[data-mc-carrusel]:not([data-listo])')) {
    carrusel.setAttribute('data-listo', '');
    const pista = /** @type {HTMLElement | null} */ (carrusel.querySelector('[data-mc-pista]'));
    if (!pista) continue;
    const piezas = /** @type {HTMLElement[]} */ (Array.from(pista.children));
    const puntos = carrusel.querySelector('[data-mc-puntos]');
    const pestanas = Array.from(carrusel.querySelectorAll('[data-mc-ir]'));
    const suave = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

    if (puntos) puntos.replaceChildren(...piezas.map(() => document.createElement('span')));

    const actual = () => {
      const borde = pista.getBoundingClientRect().left;
      let mejor = 0;
      let distancia = Infinity;
      piezas.forEach((pieza, i) => {
        const d = Math.abs(pieza.getBoundingClientRect().left - borde);
        if (d < distancia) {
          distancia = d;
          mejor = i;
        }
      });
      return mejor;
    };

    /** @param {number} indice */
    const ir = (indice) => {
      const pieza = piezas[(indice + piezas.length) % piezas.length];
      if (!pieza) return;
      const salto = pieza.getBoundingClientRect().left - pista.getBoundingClientRect().left;
      pista.scrollBy({ left: salto, behavior: suave() });
    };

    const marcar = () => {
      const indice = actual();
      Array.from(puntos?.children ?? []).forEach((punto, i) => punto.classList.toggle('activo', i === indice));
      pestanas.forEach((pestana) =>
        pestana.setAttribute('aria-pressed', String(Number(pestana.getAttribute('data-mc-ir')) === indice))
      );
      piezas.forEach((pieza, i) => pieza.classList.toggle('activa', i === indice));
    };

    let pendiente = 0;
    pista.addEventListener('scroll', () => {
      cancelAnimationFrame(pendiente);
      pendiente = requestAnimationFrame(marcar);
    });
    carrusel.querySelector('[data-mc-prev]')?.addEventListener('click', () => ir(actual() - 1));
    carrusel.querySelector('[data-mc-next]')?.addEventListener('click', () => ir(actual() + 1));
    pestanas.forEach((pestana) => pestana.addEventListener('click', () => ir(Number(pestana.getAttribute('data-mc-ir')))));
    piezas.forEach((pieza, i) =>
      pieza.addEventListener('click', (evento) => {
        if (pieza.classList.contains('activa') || !pista.hasAttribute('data-mc-tocar')) return;
        evento.preventDefault();
        ir(i);
      })
    );
    marcar();
  }
}

/** Reseña destacada junto al pedido: "Ver otra reseña". */
function iniciarCitas() {
  for (const caja of document.querySelectorAll('[data-citas]:not([data-listo])')) {
    caja.setAttribute('data-listo', '');
    const citas = /** @type {HTMLElement[]} */ (Array.from(caja.querySelectorAll('[data-cita]')));
    caja.querySelector('[data-cita-otra]')?.addEventListener('click', () => {
      const visible = citas.findIndex((cita) => !cita.hidden);
      citas.forEach((cita, i) => (cita.hidden = i !== (visible + 1) % citas.length));
    });
  }
}

/** Filtro de reseñas por tema. */
function iniciarFiltros() {
  for (const seccion of document.querySelectorAll('[data-mc-resenas]:not([data-listo])')) {
    seccion.setAttribute('data-listo', '');
    const botones = Array.from(seccion.querySelectorAll('[data-mc-filtro]'));
    const tarjetas = /** @type {HTMLElement[]} */ (Array.from(seccion.querySelectorAll('[data-categoria]')));
    const pista = seccion.querySelector('[data-mc-resenas-pista]');

    /** @param {string} filtro */
    const aplicar = (filtro) => {
      for (const boton of botones) boton.setAttribute('aria-pressed', String(boton.getAttribute('data-mc-filtro') === filtro));
      for (const tarjeta of tarjetas) {
        tarjeta.hidden = filtro === '' ? !tarjeta.hasAttribute('data-destacada') : tarjeta.getAttribute('data-categoria') !== filtro;
      }
      if (pista instanceof HTMLElement) pista.scrollLeft = 0;
    };

    botones.forEach((boton) => boton.addEventListener('click', () => aplicar(boton.getAttribute('data-mc-filtro') ?? '')));
  }
}

/** Videos: se reproducen al tocar la portada. */
function iniciarVideos() {
  for (const boton of document.querySelectorAll('[data-mc-video]:not([data-listo])')) {
    boton.setAttribute('data-listo', '');
    boton.addEventListener('click', () => {
      const video = boton.parentElement?.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;
      video.controls = true;
      video.play().catch(() => {});
      boton.parentElement?.classList.add('mc-pdp-video--andando');
    });
  }
}

/* ============================================================
 * REGION Y FECHA DE LLEGADA
 * ============================================================ */

/* El selector "Cambiar región" (abrir, lista, elegir) vive en assets/mc-base.js: la barra
 * superior sale en todo el sitio. Aqui queda solo la fecha en negrita de la pagina de producto. */
function pintarRegion() {
  const MC = window.MC;
  if (!MC || !MC.regiones?.length) return;
  const llegada = MC.llegada();
  if (!llegada) return;
  for (const nodo of document.querySelectorAll('[data-llegada-fuerte]')) {
    const plantilla = (nodo.getAttribute('data-llegada-fuerte') || '').replaceAll('[lugar]', llegada.lugar);
    const [antes, despues = ''] = plantilla.split('[fechas]');
    const fuerte = document.createElement('strong');
    fuerte.textContent = llegada.fechas;
    nodo.replaceChildren(antes, ...(plantilla.includes('[fechas]') ? [fuerte, despues] : []));
  }
}

/* ============================================================
 * ARRANQUE
 * ============================================================ */

function iniciar() {
  for (const raiz of document.querySelectorAll('[data-mc-pdp]:not([data-listo])')) {
    if (!(raiz instanceof HTMLElement)) continue;
    raiz.setAttribute('data-listo', '');
    new Pedido(raiz);
    iniciarGaleria(raiz);
    iniciarBarra(raiz);
  }
  iniciarCarruseles();
  iniciarCitas();
  iniciarFiltros();
  iniciarVideos();
  pintarRegion();
}

if (!window.mcPdpListo) {
  window.mcPdpListo = true;

  document.addEventListener('mc:region', pintarRegion);
  /* En el editor de temas las secciones se vuelven a pintar. */
  document.addEventListener('shopify:section:load', iniciar);
}

iniciar();
